import { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowLeft, LogOut, Menu, MessageCircle, Settings, X } from 'lucide-react';
import {
  PRIMARY_NAV,
  SETTINGS_GROUPS,
  isSettingsTab,
  navItem,
  type NavItem,
  type Tab,
} from '@/src/components/admin/navConfig';
import ThemeToggle from '@/src/components/ThemeToggle';

const DRAWER_MS = 200;

type Props = {
  tenantName: string;
  isOwner: boolean;
  activeTab: Tab;
  onSelect: (tab: Tab) => void;
  onSupport: () => void;
  onLogout: () => void;
};

/** Up to two initials, so the account button reads as this salon's. */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  return parts
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() ?? '')
    .join('');
}

const tabClass = (active: boolean) =>
  `border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
    active
      ? 'border-brand-600 text-accent'
      : 'border-transparent text-fg-muted hover:border-strong hover:text-fg-secondary'
  }`;

// Menu items sit outside the tab order (arrows move between them), so `focus:`
// rather than `focus-visible:` — the highlight has to follow programmatic focus.
const menuItemClass =
  'flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-fg-secondary outline-none hover:bg-canvas focus:bg-canvas';

const drawerItemClass = (active: boolean) =>
  `flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
    active ? 'bg-accent-soft text-accent' : 'text-fg-secondary hover:bg-canvas'
  }`;

export default function AdminNav({
  tenantName,
  isOwner,
  activeTab,
  onSelect,
  onSupport,
  onLogout,
}: Props) {
  const inSettings = isOwner && isSettingsTab(activeTab);

  // Account dropdown (desktop). role="menu" is a promise to keyboard users, so
  // this implements what it promises: focus lands inside on open, arrows move
  // between items, Escape closes and hands focus back, and Tab dismisses it on
  // the way out.
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuListRef = useRef<HTMLDivElement>(null);
  const accountRef = useRef<HTMLButtonElement>(null);

  const closeMenu = (restoreFocus = false) => {
    setMenuOpen(false);
    if (restoreFocus) accountRef.current?.focus();
  };

  useEffect(() => {
    if (!menuOpen) return;
    const items = (): HTMLElement[] =>
      Array.from(menuListRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? []);
    items()[0]?.focus();

    const onMouseDown = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) closeMenu();
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeMenu(true);
        return;
      }
      // Let Tab do its normal thing; a menu that stays open behind the focus
      // ring is the bug this closes.
      if (e.key === 'Tab') {
        closeMenu();
        return;
      }
      const list = items();
      if (list.length === 0) return;
      const current = list.indexOf(document.activeElement as HTMLElement);
      const focusAt = (index: number) => {
        e.preventDefault();
        list[(index + list.length) % list.length]?.focus();
      };
      // A -1 means focus sat outside the menu: enter from whichever end was asked for.
      if (e.key === 'ArrowDown') focusAt(current < 0 ? 0 : current + 1);
      else if (e.key === 'ArrowUp') focusAt(current < 0 ? list.length - 1 : current - 1);
      else if (e.key === 'Home') focusAt(0);
      else if (e.key === 'End') focusAt(list.length - 1);
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen]);

  // Drawer (mobile). `mounted` keeps it out of the DOM when closed — that also
  // stops the desktop and mobile menus from exposing duplicate button names.
  // `shown` drives the slide, flipped a frame after mount so the transition runs.
  const [mounted, setMounted] = useState(false);
  const [shown, setShown] = useState(false);
  const burgerRef = useRef<HTMLButtonElement>(null);
  const drawerRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  // The unmount is deferred so the slide-out can play, which leaves a timer in
  // flight. Escape returns focus to the burger, so a keyboard user can reopen
  // inside that window — without cancelling, the stale timer unmounts the
  // drawer they just opened, and `shown` stays true so the next open doesn't
  // animate either.
  const unmountTimer = useRef<number | null>(null);
  const cancelUnmount = useCallback(() => {
    if (unmountTimer.current !== null) {
      clearTimeout(unmountTimer.current);
      unmountTimer.current = null;
    }
  }, []);
  useEffect(
    () => () => {
      if (unmountTimer.current !== null) clearTimeout(unmountTimer.current);
    },
    [],
  );

  const openDrawer = () => {
    cancelUnmount();
    setMounted(true);
    requestAnimationFrame(() => setShown(true));
  };
  const closeDrawer = useCallback(() => {
    setShown(false);
    cancelUnmount();
    unmountTimer.current = window.setTimeout(() => {
      unmountTimer.current = null;
      setMounted(false);
    }, DRAWER_MS);
    burgerRef.current?.focus();
  }, [cancelUnmount]);

  // Lock the page behind the drawer. Only `overflow` — a fixed body fights the
  // iOS keyboard, and index.css already forces 16px inputs to stop zoom.
  useEffect(() => {
    if (!mounted) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [mounted]);

  // Rotating to a tablet width hides the drawer via `md:hidden` but would leave
  // it "open" — and the body locked — with nothing on screen to explain why.
  useEffect(() => {
    if (!mounted) return;
    const desktop = window.matchMedia('(min-width: 768px)');
    const onChange = () => {
      if (desktop.matches) {
        cancelUnmount();
        setShown(false);
        setMounted(false);
      }
    };
    desktop.addEventListener('change', onChange);
    return () => desktop.removeEventListener('change', onChange);
  }, [mounted, cancelUnmount]);

  useEffect(() => {
    if (!mounted) return;
    closeRef.current?.focus();
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeDrawer();
        return;
      }
      if (e.key !== 'Tab' || !drawerRef.current) return;
      // Keep Tab inside the dialog, as aria-modal promises.
      const focusable = drawerRef.current.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [mounted, closeDrawer]);

  const go = (tab: Tab) => {
    onSelect(tab);
    closeDrawer();
  };

  const visible = (item: NavItem) => isOwner || !item.ownerOnly;
  const primary = PRIMARY_NAV.filter(visible);

  const renderDrawerItem = (item: NavItem) => {
    const Icon = item.icon;
    return (
      <button
        key={item.id}
        onClick={() => go(item.id)}
        aria-current={activeTab === item.id ? 'page' : undefined}
        className={drawerItemClass(activeTab === item.id)}
      >
        <Icon className="h-4 w-4" />
        {item.label}
      </button>
    );
  };

  return (
    <>
      <header className="border-default bg-surface/80 sticky top-0 z-40 border-b backdrop-blur-md">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-14 items-center justify-between sm:h-16">
            {/* Left: branding */}
            <div className="flex min-w-0 items-center">
              <span className="text-fg shrink-0 text-lg font-bold sm:text-xl">Calendyz Pro</span>
              <span className="bg-muted text-fg-secondary ml-2 max-w-[120px] truncate rounded-full px-2 py-0.5 text-xs font-medium sm:ml-4 sm:max-w-none sm:px-3 sm:py-1 sm:text-sm">
                {tenantName}
              </span>
            </div>

            {/* Desktop: tabs (or a way back out of settings) + account menu */}
            <div className="hidden items-center gap-6 md:flex">
              {inSettings ? (
                <button
                  onClick={() => onSelect('calendar')}
                  className="hover:text-accent text-fg-muted flex items-center gap-1.5 text-sm font-medium transition-colors"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to calendar
                </button>
              ) : (
                <div className="flex items-center">
                  {primary.map((item) => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.id}
                        onClick={() => onSelect(item.id)}
                        aria-current={activeTab === item.id ? 'page' : undefined}
                        className={tabClass(activeTab === item.id)}
                      >
                        <Icon className="-mt-0.5 mr-1.5 inline h-4 w-4" />
                        {item.label}
                      </button>
                    );
                  })}
                </div>
              )}

              <div className="flex items-center gap-4">
                <ThemeToggle variant="desktop" />
                {isOwner && (
                  <button
                    onClick={() => onSelect('salon')}
                    aria-current={inSettings ? 'page' : undefined}
                    className={tabClass(inSettings)}
                  >
                    <Settings className="-mt-0.5 mr-1.5 inline h-4 w-4" />
                    Settings
                  </button>
                )}

                <div className="relative" ref={menuRef}>
                  <button
                    ref={accountRef}
                    onClick={() => setMenuOpen((v) => !v)}
                    aria-label="Account menu"
                    aria-expanded={menuOpen}
                    aria-haspopup="menu"
                    aria-controls={menuOpen ? 'admin-account-menu' : undefined}
                    className="bg-accent-soft text-accent hover:bg-accent-soft focus:ring-brand-600 flex h-9 w-9 items-center justify-center rounded-full text-xs font-semibold transition-colors outline-none focus:ring-2"
                  >
                    {initials(tenantName)}
                  </button>
                  {menuOpen && (
                    <div
                      id="admin-account-menu"
                      ref={menuListRef}
                      role="menu"
                      aria-label="Account menu"
                      className="border-subtle bg-surface absolute right-0 z-50 mt-2 w-48 overflow-hidden rounded-xl border py-1 shadow-lg"
                    >
                      {isOwner && (
                        <button
                          role="menuitem"
                          tabIndex={-1}
                          onClick={() => {
                            closeMenu();
                            onSelect('account');
                          }}
                          className={menuItemClass}
                        >
                          <NavIcon id="account" />
                          Account
                        </button>
                      )}
                      <button
                        role="menuitem"
                        tabIndex={-1}
                        onClick={() => {
                          closeMenu();
                          onSupport();
                        }}
                        className={menuItemClass}
                      >
                        <MessageCircle className="text-fg-subtle h-4 w-4" />
                        Support
                      </button>
                      <div className="border-subtle my-1 border-t" />
                      <button
                        role="menuitem"
                        tabIndex={-1}
                        onClick={() => {
                          closeMenu();
                          onLogout();
                        }}
                        className={menuItemClass}
                      >
                        <LogOut className="text-fg-subtle h-4 w-4" />
                        Logout
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Mobile: burger */}
            <button
              ref={burgerRef}
              onClick={openDrawer}
              aria-label="Open menu"
              aria-expanded={mounted}
              // The drawer is only in the DOM while open, so only point at it then.
              aria-controls={mounted ? 'admin-drawer' : undefined}
              className="text-fg-muted hover:bg-muted hover:text-fg-secondary -mr-2 rounded-lg p-2 transition-colors md:hidden"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      {/* The drawer lives outside <header> on purpose: backdrop-filter makes an
          element a containing block for fixed descendants, so nesting it there
          would size it to the 56px bar instead of the viewport. */}
      {mounted && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div
            onClick={closeDrawer}
            className={`absolute inset-0 bg-black/40 transition-opacity duration-200 motion-reduce:transition-none ${
              shown ? 'opacity-100' : 'opacity-0'
            }`}
          />
          <div
            id="admin-drawer"
            ref={drawerRef}
            role="dialog"
            aria-modal="true"
            aria-label="Dashboard menu"
            className={`bg-surface absolute inset-y-0 right-0 flex w-[85%] max-w-xs flex-col shadow-xl transition-transform duration-200 motion-reduce:transition-none ${
              shown ? 'translate-x-0' : 'translate-x-full'
            }`}
          >
            {/* The one place the admin shell speaks in the brand's display face:
                whose shop you're in, answered the moment the drawer opens. */}
            <div className="bg-accent-soft flex items-start justify-between gap-3 px-5 py-4">
              <div className="min-w-0">
                <p className="font-display text-fg truncate text-lg font-semibold">{tenantName}</p>
                <p className="text-accent mt-0.5 text-xs font-medium">
                  {isOwner ? 'Owner' : 'Worker'}
                </p>
              </div>
              <button
                ref={closeRef}
                onClick={closeDrawer}
                aria-label="Close menu"
                className="hover:bg-accent-soft text-fg-muted hover:text-fg-secondary -mr-2 rounded-lg p-2 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <nav className="flex-1 overflow-y-auto px-3 py-3">
              <div className="space-y-1">{primary.map(renderDrawerItem)}</div>

              {isOwner &&
                SETTINGS_GROUPS.map((group) => (
                  <div key={group.heading}>
                    <p className="text-fg-subtle px-3 pt-4 pb-1 text-xs font-semibold tracking-wider uppercase">
                      {group.heading}
                    </p>
                    <div className="space-y-1">{group.items.map(renderDrawerItem)}</div>
                  </div>
                ))}

              <div className={isOwner ? 'mt-1 space-y-1' : 'mt-4 space-y-1'}>
                {!isOwner && (
                  <p className="text-fg-subtle px-3 pb-1 text-xs font-semibold tracking-wider uppercase">
                    Your account
                  </p>
                )}
                <button
                  onClick={() => {
                    closeDrawer();
                    onSupport();
                  }}
                  className={drawerItemClass(false)}
                >
                  <MessageCircle className="h-4 w-4" />
                  Support
                </button>
              </div>
            </nav>

            <div className="border-subtle space-y-3 border-t px-3 py-3">
              <ThemeToggle variant="mobile" />
              <button
                onClick={() => {
                  closeDrawer();
                  onLogout();
                }}
                className={drawerItemClass(false)}
              >
                <LogOut className="h-4 w-4" />
                Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/** Renders a nav item's own icon, muted, for the account dropdown. */
function NavIcon({ id }: { id: Tab }) {
  const Icon = navItem(id)?.icon;
  return Icon ? <Icon className="text-fg-subtle h-4 w-4" /> : null;
}
